import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bot, HelpCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { HelpTooltip } from '../shared';
import { ChatbotConfig } from '@/services/chatbotService';

interface BasicSettingsProps {
  config: ChatbotConfig;
  onUpdate: (updates: Partial<ChatbotConfig>) => void;
}

export function BasicSettings({ config, onUpdate }: BasicSettingsProps) {
  const { t } = useTranslation('business', { keyPrefix: 'communication' });
  
  return (
    <Card className="border border-slate-200/60 shadow-md shadow-slate-900/5 bg-white hover:shadow-lg hover:shadow-slate-900/8 transition-shadow duration-200">
      <CardHeader className="pb-4 border-b border-slate-100/80">
        <CardTitle className="flex items-center gap-2 text-lg">
          <div className="w-8 h-8 rounded-lg bg-blue-100 border border-blue-200/50 flex items-center justify-center shadow-sm">
            <Bot className="w-4 h-4 text-blue-600" />
          </div>
          {t('basicSettings')}
          <HelpTooltip content={t('basicSettingsHelp')}>
            <HelpCircle className="w-4 h-4 text-slate-400 cursor-help" />
          </HelpTooltip>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5 pt-6">
        <div className="space-y-2">
          <Label htmlFor="botName">{t('assistantName')}</Label>
          <Input
            id="botName"
            value={config.botName}
            onChange={e => onUpdate({ botName: e.target.value })}
            placeholder={t('assistantNamePlaceholder')}
            className="focus:ring-primary focus:border-primary border-slate-200/80 shadow-sm"
          />
          <p className="text-xs text-slate-600">
            {t('assistantNameHelp')}
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="welcomeMessage">{t('welcomeMessageField')}</Label>
          <Textarea
            id="welcomeMessage"
            value={config.welcomeMessage}
            onChange={e => onUpdate({ welcomeMessage: e.target.value })}
            placeholder={t('welcomeMessagePlaceholder')}
            rows={3}
            className="focus:ring-primary focus:border-primary resize-none border-slate-200/80 shadow-sm"
          />
          <p className="text-xs text-slate-600">
            {t('welcomeMessageHelp')}
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="position">{t('positionOnWebsite')}</Label>
          <Select
            value={config.position}
            onValueChange={(value) => onUpdate({ position: value as any })}
          >
            <SelectTrigger className="focus:ring-primary focus:border-primary border-slate-200/80 shadow-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="bottom-right">{t('bottomRight')}</SelectItem>
              <SelectItem value="bottom-left">{t('bottomLeft')}</SelectItem>
              <SelectItem value="top-right">{t('topRight')}</SelectItem>
              <SelectItem value="top-left">{t('topLeft')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardContent>
    </Card>
  );
} 